/**
 * 公式マスターから避難先 (避難場所・避難所) を選ぶ純関数群。
 *
 * autodraft.ts から切り出した。Vite に依存しない (`@/services/*` を実行時に
 * import しない。型の import のみ) ので、tsx のスクリプトからも同じ選定を使える。
 * マスターと地区割当の読み込みは呼び出し側 (autodraft.ts の pickOfficialDestinations) が行う。
 */
import type { LngLat } from '@/types/property';
import type { EvacuationCategory } from '@/types/evacuation';
import type { EvacuationPlaceExtended } from '@/services/evacuationData';
import { haversineMeters } from '@/lib/geo/distance';
import { normalizeFacilityName } from '@/lib/knowledge/normalize';
import { pickEvacuationArea } from './open-space';
import type { AssignmentResult } from './official-assignment';

/** マスターの place の代表座標 (point 優先、無ければ最初のリング重心)。 */
export function placeCoord(place: EvacuationPlaceExtended): LngLat | null {
  if (place.point) return place.point;
  const ring = place.polygon?.coordinates?.[0];
  if (ring && ring.length > 0) {
    const s = ring.reduce(
      (a, [lng, lat]) => ({ lng: a.lng + lng, lat: a.lat + lat }),
      { lng: 0, lat: 0 },
    );
    return { lng: s.lng / ring.length, lat: s.lat / ring.length };
  }
  return null;
}

/**
 * 47 都道府県名。住所の空白を詰めると「〒260-0026千葉県」の郵便番号の数字が
 * 「2〜3 文字 + 県」に掛かるので、県名は実在の名前で探す。
 */
const PREFECTURE_RE =
  /(北海道|東京都|京都府|大阪府|(?:青森|岩手|宮城|秋田|山形|福島|茨城|栃木|群馬|埼玉|千葉|神奈川|新潟|富山|石川|福井|山梨|長野|岐阜|静岡|愛知|三重|滋賀|兵庫|奈良|和歌山|鳥取|島根|岡山|広島|山口|徳島|香川|愛媛|高知|福岡|佐賀|長崎|熊本|大分|宮崎|鹿児島|沖縄)県)/;

/** 住所の区切りの空白 (「横浜市 鶴見区」) を落とす。残すと市区町村名に空白が入る。 */
const compact = (address: string) => address.replace(/[\s\u3000]/g, '');

/** 住所から都道府県を取り出す (郵便番号や建物名が前後にあってもよい)。 */
export function prefectureOfAddress(address: string): string | null {
  return compact(address).match(PREFECTURE_RE)?.[1] ?? null;
}

/**
 * 住所から市区町村を取り出す。政令指定都市は「横浜市鶴見区」のように市+区で返す
 * (避難先マスターの municipality がこの形。東京23区は「大田区」)。
 */
export function municipalityOfAddress(address: string): string | null {
  const s = compact(address);
  const pref = s.match(PREFECTURE_RE);
  const after = pref ? s.slice(pref.index! + pref[1]!.length) : s;
  // 最初に現れる市区町村で切る。「市」を先に探すと、町名に市を含む特別区の住所
  // (新宿区市谷加賀町) が「新宿区市」になり、同じ区の避難所の判定が外れていた。
  const first = after.match(/^(.+?[市区町村])/)?.[1];
  if (!first) return null;
  if (!first.endsWith('市')) return first;
  // 政令市は「横浜市鶴見区」のように市+区 (区名は短いので長さで縛り、町名の区は拾わない)
  const ward = after.slice(first.length).match(/^(.{1,4}?区)/)?.[1];
  return ward ? `${first}${ward}` : first;
}

/**
 * 避難先がどの市区町村のものかを読む。
 *
 * マスターの municipality が空の施設 (区をまたぐ広域避難場所など) では、それだけに
 * 頼ると多摩地域の市境で隣の市の学校を掴む。施設名の設置主体
 * 「武蔵野市立桜野小学校」からも市区町村を読む (「区立◯◯小学校」のように
 * 区名が無いものは読めないので null = 判定しない)。
 */
function municipalityOfPlace(p: EvacuationPlaceExtended): string | null {
  if (p.municipality) return p.municipality;
  return /([^\s\u3000]{2,6}?[市区町村])立/.exec(p.name)?.[1] ?? null;
}

/** 名称マッチ用の正規化。契約は knowledge/normalize.ts に集約 (異体字 靭⇔靱 等も吸収)。 */
const normalizeName = normalizeFacilityName;

type PlaceWithDist = { p: EvacuationPlaceExtended; c: LngLat; d: number };

/**
 * 名前で指定された避難先を探す距離の上限 (m)。
 *
 * 同じ名前の学校・公園は全国にある。正規化は括弧書きを落とすので
 * 「第五中学校（校舎・体育館）」のような**遠方の同名校**が完全一致になり、
 * 近所の「中野区立第五中学校」(部分一致) を押しのけていた
 * (実測: 中野上高田で 12.6km、横浜の豊岡小学校で 51km 先の同名校を掴んだ)。
 * 徒歩の避難先としてあり得ない距離の一致は、一致の種類に関係なく後回しにする。
 */
const NAME_MATCH_MAX_M = 5000;

/**
 * 名前で公式マスターの施設を1件特定する (近くの完全一致 → 近くの部分一致 → 遠方)。
 * 見つからなければ null。取込時の「避難先を名前で指定」用。
 *
 * @param preferCategory 同名が複数あるときに優先する種別。マスターには同じ場所が
 *   別種別で二重に載っていることがあり (例「桐ケ丘・赤羽台・西が丘地区」は
 *   広域避難場所と指定緊急避難場所の2件。前者だけが都の公式エリア形状2面を持つ)、
 *   役割に合う種別を選ばないと形状の粗いほうを拾ってしまう。
 */
function findPlaceByName(
  candidates: PlaceWithDist[],
  name: string,
  preferCategory?: EvacuationCategory,
): PlaceWithDist | null {
  const target = normalizeName(name);
  if (!target) return null;
  const byDist = (a: PlaceWithDist, b: PlaceWithDist) => a.d - b.d;
  const exact = candidates
    .filter((x) => normalizeName(x.p.name) === target)
    .sort(byDist);
  const partial = candidates
    .filter((x) => {
      const n = normalizeName(x.p.name);
      return n.includes(target) || target.includes(n);
    })
    .sort(byDist);
  const near = (xs: PlaceWithDist[]) => xs.filter((x) => x.d <= NAME_MATCH_MAX_M);
  const preferred = (xs: PlaceWithDist[]) =>
    preferCategory
      ? xs.filter((x) => (x.p.categories ?? [x.p.category]).includes(preferCategory))
      : [];
  // 近い一致の中では「役割に合う種別」を優先し、そのあと完全一致 → 部分一致。
  // どれも近くに無ければ従来どおり遠方でも返す (マスターに近傍が無い地域の救済)。
  return (
    preferred(near(exact))[0] ??
    near(exact)[0] ??
    preferred(near(partial))[0] ??
    near(partial)[0] ??
    exact[0] ??
    partial[0] ??
    null
  );
}

/**
 * 公式の地区割当が指す広域避難場所をマスターから引く (機械の割当専用の厳密な照合)。
 *
 * findPlaceByName は人が打った名前向けで、部分一致や遠方の同名まで拾う。割当に使うと
 * 横浜の割当名 16 件 (町丁目 88 行) が遠方の同名・部分一致に化け、しかも根拠には
 * 「公式の割当」と書かれていた (実測: 総持寺 → 大阪府茨木市の「総持寺いのち・愛・ゆめ
 * センター」/ 高田小学校一帯 → 名古屋市 / 舞岡公園 → 広島の「岡公園」/ 金井公園 → 小金井公園)。
 * 割当はマスターと同じ公式の出典から来るので名前は一致するはず。次の 3 つをすべて満たす
 * 最寄りだけを採る: 正規化した名前の完全一致・広域避難場所・NAME_MATCH_MAX_M 以内。
 * 無ければ null (呼び出し側は自動選定に落とし、その旨を根拠に残す)。
 */
function findAssignedPlace(
  candidates: PlaceWithDist[],
  placeName: string,
): PlaceWithDist | null {
  const target = normalizeName(placeName);
  if (!target) return null;
  return (
    candidates
      .filter(
        (x) =>
          x.d <= NAME_MATCH_MAX_M &&
          (x.p.categories ?? [x.p.category]).includes('広域避難場所') &&
          normalizeName(x.p.name) === target,
      )
      .sort((a, b) => a.d - b.d)[0] ?? null
  );
}

/**
 * 施設名から「指定福祉避難所」らしさを判定する保険 (shelterFor が空の自治体向け)。
 * GSI データは市区町村によって受入対象者列を埋めていないことがあるため、
 * 明らかに要配慮者向けと分かる施設名も併用する。
 */
export const WELFARE_NAME_RE = /老人ホーム|特別養護|老人保健|介護|ケアハウス|グループホーム|デイサービス|障害者|障がい者/;

/**
 * 一般の住民向けではない「指定福祉避難所」か。
 *
 * nmf-83 (浦安): 自動選定が「特別養護老人ホームしずか荘」(166m、受入対象者=要配慮者) を
 * 一般の避難所として選び、納品 (北部小学校, 928m) と食い違った。国土地理院データの
 * 受入対象者列 (「受け入れる被災者等を特定する場合に記入 (指定福祉避難所のみ)」) を
 * 第一の目印にし、それが空の自治体向けに明らかな施設名も併用する。
 */
export function isWelfareShelter(p: { name: string; shelterFor?: string }): boolean {
  return !!p.shelterFor || WELFARE_NAME_RE.test(p.name);
}

export interface PickOptions {
  evacuationName?: string;
  shelterName?: string;
  prefecture?: string | null;
  municipality?: string | null;
}

/** 選定の経緯を構造化したもの (根拠の文字列と同じ内容を、機械で扱える形で)。 */
export interface PickDetail {
  evacuation: {
    via: 'named' | 'assigned' | 'auto';
    /** 名前指定があったがマスターに無い */
    namedMissing: boolean;
    /** lookupAssignment の kind (データ無しは null) */
    assignment: 'assigned' | 'ambiguous' | 'unassigned' | null;
    /** 割当先がマスターで引けなかった */
    assignedMissing: boolean;
    /** 名前指定が公式の割当と違う */
    officialDiffers: boolean;
  };
  shelter: {
    via: 'named' | 'auto';
    namedMissing: boolean;
    /** categories に 指定避難所 */
    designated: boolean;
    /** isWelfareShelter */
    welfare: boolean;
    relation: 'same' | 'other' | 'unknown';
  };
}

export interface PickResult {
  evacuationArea: EvacuationPlaceExtended;
  shelter: EvacuationPlaceExtended;
  /** 避難場所を選んだ根拠 (sourceNote に入る)。②の要確認リストの材料。 */
  evacuationNote: string;
  /** 避難所を選んだ根拠 (sourceNote に入る)。 */
  shelterNote: string;
  /** 選定の経緯 (取込の要確認一覧の材料)。 */
  detail: PickDetail;
}

/**
 * 公式マスターから避難先 2 件を選ぶ (純関数)。
 *   - 避難場所: 名前指定 > 公式の地区割当 > 広域避難場所・大火災対応・広い屋外の近いほう
 *   - 避難所:   名前指定 > 同じ市区町村の指定避難所 > 同じ市区町村の施設 > 指定避難所 > 最寄り
 * 選んだ根拠を文字列で返す (避難先の sourceNote に残す)。
 * どちらも編集ページのピッカーで後から変更できる「下書き」。
 */
export function selectOfficialDestinations(
  places: EvacuationPlaceExtended[],
  propertyCoords: LngLat,
  opts: PickOptions = {},
  assignment: AssignmentResult = null,
): PickResult {
  const all: PlaceWithDist[] = places
    .map((p) => ({ p, c: placeCoord(p) }))
    .filter((x): x is { p: EvacuationPlaceExtended; c: LngLat } => x.c !== null)
    .map((x) => ({ ...x, d: haversineMeters(propertyCoords, x.c) }))
    .sort((a, b) => a.d - b.d);
  if (all.length === 0) {
    throw new Error('近隣に公式の避難場所データがありません (マスター未整備の地域)');
  }
  // 避難先を指定するのは市区町村なので、候補は原則として**同じ都道府県**から選ぶ。
  // 県境の物件で、川を渡った先の学校が「最寄り」として選ばれていた
  // (武蔵小杉→大田区の嶺町小学校 2.4km / 浦安→江戸川区の瑞江中学校 2.2km /
  //  川口→北区の西浮間小学校 4.7km)。同県に候補が無い地域では全件に戻す。
  const inPref = opts.prefecture ? all.filter((x) => x.p.prefecture === opts.prefecture) : [];
  const pool = inPref.length > 0 ? inPref : all;
  // 自動選定は近傍300件から。名前指定はプール全件から (遠方でも名前で特定できる)。
  const near = pool.slice(0, 300);
  const cats = (p: EvacuationPlaceExtended) => p.categories ?? [p.category];

  // --- 避難場所 ---
  const evacNotes: string[] = [];
  // 名指しは全件から探す。都道府県で絞るのは自動選定のためで、
  // 明示的に名前を指定したときまで縛ると県境の物件で指定が効かない
  // (川崎市麻生区の物件に稲城市の平尾近隣公園を指定できなかった)。
  const named = opts.evacuationName ? findPlaceByName(all, opts.evacuationName, '広域避難場所') : null;
  if (opts.evacuationName && !named) evacNotes.push(`名前指定「${opts.evacuationName}」がマスターに無い`);
  let assigned: PlaceWithDist | null = null;
  // 先方の指定が公式の割当と違うときは両方を残す (指定と公式の食い違いを後で洗い出す材料)。
  // 同じ場所かは、打たれた名前・マスター上の名前のどちらかが割当名と一致するかで見る。
  const official = assignment?.kind === 'assigned' ? assignment.placeName : null;
  const sameAsOfficial =
    official !== null &&
    !!named &&
    [opts.evacuationName ?? '', named.p.name].some(
      (n) => normalizeName(n) === normalizeName(official),
    );
  if (named) {
    evacNotes.push(
      official && !sameAsOfficial
        ? `名前指定: ${opts.evacuationName} (公式の割当: ${official})`
        : `名前指定: ${opts.evacuationName}`,
    );
  } else if (assignment?.kind === 'assigned') {
    assigned = findAssignedPlace(all, assignment.placeName);
    evacNotes.push(assigned ? assignment.basis : `${assignment.basis} (マスターに無く自動選定)`);
  } else if (assignment) {
    evacNotes.push(`自動選定 (${assignment.basis})`);
  } else {
    evacNotes.push('自動選定');
  }
  const evac =
    named ??
    assigned ??
    // 広域避難場所 と「屋外の広い指定緊急避難場所」の近いほう。
    // 広域避難場所だけを見ていた頃、千代田区 (区内全域が地区内残留地区で広域避難場所が
    // 1つも無い) の物件が 589m の北の丸公園を飛ばして 1,588m の後楽園一帯を指していた。
    pickEvacuationArea(near) ??
    near.find((x) => x.p.disasterTypes.includes('大火災')) ??
    near[0]!;

  // --- 避難所 ---
  // 避難所は住民の市区町村が指定するので、自分の市区町村のものを優先する。
  // 都県をそろえても、市境の物件では隣の市の学校が最寄りになる
  // (浦安市当代島 → 市川市の新井小学校 476m / 小金井市梶野町 → 武蔵野市立桜野小学校 543m)。
  const cityRelation = (x: PlaceWithDist): 'same' | 'other' | 'unknown' => {
    const mine = opts.municipality;
    const theirs = municipalityOfPlace(x.p);
    if (!mine || !theirs) return 'unknown';
    // 政令市は「横浜市鶴見区」と「横浜市」の表記ゆれがあるので前方一致で見る
    return mine.startsWith(theirs) || theirs.startsWith(mine) ? 'same' : 'other';
  };
  // どちらか分からなければ (unknown) 距離だけで選ぶ
  const sameCity = (x: PlaceWithDist) => cityRelation(x) !== 'other';
  const namedShelter = opts.shelterName ? findPlaceByName(all, opts.shelterName, '指定避難所') : null;
  // 指定避難所の各段で、まず一般の住民向け (福祉避難所でない) を優先する。
  // 名前で明示的に指定された場合は上書きしない (namedShelter が最優先のまま)。
  const general = (x: PlaceWithDist) => !isWelfareShelter(x.p);
  const shelter =
    namedShelter ??
    near.find((x) => x.p !== evac.p && sameCity(x) && cats(x.p).includes('指定避難所') && general(x)) ??
    near.find((x) => x.p !== evac.p && sameCity(x) && cats(x.p).includes('指定避難所')) ??
    near.find((x) => x.p !== evac.p && sameCity(x)) ??
    near.find((x) => x.p !== evac.p && cats(x.p).includes('指定避難所') && general(x)) ??
    near.find((x) => x.p !== evac.p && cats(x.p).includes('指定避難所')) ??
    near.find((x) => x.p !== evac.p) ??
    evac;
  const shelterNotes: string[] = [];
  if (opts.shelterName && !namedShelter) shelterNotes.push(`名前指定「${opts.shelterName}」がマスターに無い`);
  if (namedShelter) {
    shelterNotes.push(`名前指定: ${opts.shelterName}`);
  } else {
    const kind = !cats(shelter.p).includes('指定避難所')
      ? '指定避難所ではない施設'
      : isWelfareShelter(shelter.p)
        ? '福祉避難所'
        : '指定避難所';
    const rel = { same: '同じ市区町村', other: '別の市区町村', unknown: '市区町村不明' }[cityRelation(shelter)];
    shelterNotes.push(`自動選定 (${kind}・${rel})`);
  }

  const detail: PickDetail = {
    evacuation: {
      via: named ? 'named' : assigned ? 'assigned' : 'auto',
      namedMissing: !!opts.evacuationName && !named,
      assignment: assignment?.kind ?? null,
      assignedMissing: assignment?.kind === 'assigned' && !named && !assigned,
      officialDiffers: !!named && official !== null && !sameAsOfficial,
    },
    shelter: {
      via: namedShelter ? 'named' : 'auto',
      namedMissing: !!opts.shelterName && !namedShelter,
      designated: cats(shelter.p).includes('指定避難所'),
      welfare: isWelfareShelter(shelter.p),
      relation: cityRelation(shelter),
    },
  };

  return {
    evacuationArea: evac.p,
    shelter: shelter.p,
    evacuationNote: evacNotes.join('／'),
    shelterNote: shelterNotes.join('／'),
    detail,
  };
}
